/**
 * Coach Health Score — visual surface.
 *
 * Renders the 0-100 score + letter grade + trend arrow + factor breakdown.
 * Two display modes:
 *
 *   - `compact={true}`   → just the score badge (for the LiveCoachFeed header)
 *   - `compact={false}`  → full card with factor breakdown (for the Dashboard)
 *
 * Pure component — receives a fully-computed score object from
 * computeHealthScore and just renders.
 *
 * Props:
 *   health     { score, grade, factors[], trend, delta, priorScore }
 *   compact    bool
 *   darkMode   bool
 */

import { Heart, ArrowUp, ArrowDown, Minus, Sparkles } from 'lucide-react';

// Grade-tinted glass per letter — matches v7.6 engaged-chip language.
// Each entry has the per-grade ring+glow added inline at use-site so the
// score badge feels alive (color-keyed glow per grade severity).
const GRADE_COLORS = {
  A: { dark: 'text-emerald-100 bg-emerald-500/20 border-emerald-400/50 ring-1 ring-inset ring-emerald-400/25', light: 'text-emerald-800 bg-emerald-50 border-emerald-300 ring-1 ring-inset ring-emerald-200' },
  B: { dark: 'text-sky-100     bg-sky-500/20     border-sky-400/50     ring-1 ring-inset ring-sky-400/25',     light: 'text-sky-800     bg-sky-50     border-sky-300     ring-1 ring-inset ring-sky-200'     },
  C: { dark: 'text-amber-100   bg-amber-500/20   border-amber-400/50   ring-1 ring-inset ring-amber-400/25',   light: 'text-amber-800   bg-amber-50   border-amber-300   ring-1 ring-inset ring-amber-200'   },
  D: { dark: 'text-orange-100  bg-orange-500/20  border-orange-400/50  ring-1 ring-inset ring-orange-400/25',  light: 'text-orange-800  bg-orange-50  border-orange-300  ring-1 ring-inset ring-orange-200'  },
  F: { dark: 'text-red-100     bg-red-500/20     border-red-400/50     ring-1 ring-inset ring-red-400/25',     light: 'text-red-800     bg-red-50     border-red-300     ring-1 ring-inset ring-red-200'     },
};

const TrendIcon = ({ trend, darkMode }) => {
  if (trend === 'up')   return <ArrowUp size={11} className={darkMode ? 'text-emerald-400' : 'text-emerald-600'} />;
  if (trend === 'down') return <ArrowDown size={11} className={darkMode ? 'text-red-400' : 'text-red-600'} />;
  if (trend === 'flat') return <Minus size={11} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />;
  return <Sparkles size={11} className={darkMode ? 'text-purple-400' : 'text-purple-600'} />;  // first snapshot
};

const trendCopy = (health, darkMode) => {
  if (!health) return '';
  if (health.trend === 'first') return 'first snapshot';
  if (health.delta == null) return '';
  const sign = health.delta > 0 ? '+' : '';
  return `${sign}${health.delta} since last`;
};

const CoachHealthScore = ({ health, compact = false, darkMode = true }) => {
  if (!health) return null;
  const grade = health.grade || 'F';
  const colors = GRADE_COLORS[grade] || GRADE_COLORS.F;
  const colorClass = darkMode ? colors.dark : colors.light;

  // ── Compact (header pill) mode ──
  if (compact) {
    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${colorClass}`}
        title={`Coach Health Score ${health.score}/100 (${grade}) · ${trendCopy(health, darkMode)}`}
      >
        <Heart size={9} className="opacity-90" />
        <span className="tabular-nums">{health.score}</span>
        <span className="opacity-70">·</span>
        <span>{grade}</span>
        <TrendIcon trend={health.trend} darkMode={darkMode} />
      </div>
    );
  }

  // ── Full card (dashboard) mode ──
  // Frosted-glass panel surface — matches the v7.6 "panel on panel" pattern
  // used inside modals (translucent dark bg + blur + subtle white-border).
  return (
    <div
      className={`rounded-xl border p-4 backdrop-blur-sm ${
        darkMode ? 'bg-white/[0.04] border-white/10' : 'bg-white/70 border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Heart size={14} className={darkMode ? 'text-purple-300' : 'text-purple-700'} />
          <h3 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Coach Health Score
          </h3>
        </div>
        <div className={`text-[11px] flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <TrendIcon trend={health.trend} darkMode={darkMode} />
          <span>{trendCopy(health, darkMode)}</span>
          {health.priorScore != null && (
            <span className="opacity-70 font-mono tabular-nums">({health.priorScore} → {health.score})</span>
          )}
        </div>
      </div>

      {/* Score + grade hero block — grade-tinted glass with inset ring +
          color-keyed soft glow per severity. Border bumped down from 2px →
          1px since the inset ring now does the visual heavy lifting. */}
      <div className="flex items-center gap-4 mb-3">
        <div
          className={`flex flex-col items-center justify-center rounded-lg border px-3 py-2 ${colorClass}`}
          style={{
            minWidth: 92,
            boxShadow: darkMode ? `0 0 16px ${{
              A: 'rgba(16,185,129,0.18)',
              B: 'rgba(56,189,248,0.18)',
              C: 'rgba(245,158,11,0.18)',
              D: 'rgba(249,115,22,0.18)',
              F: 'rgba(239,68,68,0.18)',
            }[grade]}` : undefined,
          }}
        >
          <div className="text-3xl font-bold leading-none tabular-nums">{health.score}</div>
          <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">/ 100</div>
        </div>
        <div className="flex-1">
          <div className={`text-2xl font-bold ${colorClass.split(' ')[0]}`}>{grade}</div>
          <p className={`text-xs leading-snug mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {grade === 'A' && 'Process is humming. Edges identified, leaks contained, profile fresh.'}
            {grade === 'B' && 'Solid process with room to tighten. Look at the lowest-scoring factor below.'}
            {grade === 'C' && 'Mid-tier. Either the leaks are stacking or the profile is stale — see breakdown.'}
            {grade === 'D' && 'Multiple weak factors. Time to refresh the profile and address chronic leaks directly.'}
            {grade === 'F' && 'Profile is stale or leak-heavy. Click Refresh, then act on the top warnings.'}
          </p>
        </div>
      </div>

      {/* Factor breakdown */}
      <div className="space-y-1.5">
        {health.factors.map(f => {
          const fraction = f.max > 0 ? f.score / f.max : 0;
          const barColor = fraction >= 0.75
            ? (darkMode ? 'bg-emerald-500/60' : 'bg-emerald-500')
            : fraction >= 0.5
              ? (darkMode ? 'bg-sky-500/60' : 'bg-sky-500')
              : fraction >= 0.25
                ? (darkMode ? 'bg-amber-500/60' : 'bg-amber-500')
                : (darkMode ? 'bg-red-500/60' : 'bg-red-500');
          return (
            <div key={f.id} className="flex items-center gap-2">
              <div className={`text-[11px] w-44 shrink-0 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {f.label}
                <span className={`ml-1.5 font-mono tabular-nums ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {f.value}
                </span>
              </div>
              <div className={`flex-1 h-2 rounded-full overflow-hidden ${darkMode ? 'bg-white/5' : 'bg-gray-200'}`}>
                <div className={`h-full ${barColor} transition-[background-color,border-color,box-shadow,color] duration-150`} style={{ width: `${fraction * 100}%` }} />
              </div>
              <div className={`text-[11px] w-12 text-right font-mono tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {f.score}/{f.max}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CoachHealthScore;
